import { Calculator, History } from 'lucide-react';
import { round } from '@/utils/mathUtils';
import {
  useEffect,
  useState,
  useCallback,
  memo,
  useMemo,
  type MutableRefObject,
} from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import type { IItemData } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';
import PurchaseHistoryDialog from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/PurchaseHistoryDialog';

interface DiscountBatchSectionProps {
  selectedItem: IItemData | null;
  suppliar: boolean;
  qtyInput: string;
  taxInput: string;
  discountInput: string;
  specialDiscountInput: string;
  bonusInput: string;
  bnoInput: string;
  expireDate: string;
  discountRef: MutableRefObject<HTMLInputElement | null>;
  taxRef: MutableRefObject<HTMLInputElement | null>;
  specialDiscountRef: MutableRefObject<HTMLInputElement | null>;
  bonusRef: MutableRefObject<HTMLInputElement | null>;
  bnoRef: MutableRefObject<HTMLInputElement | null>;
  expireDateRef: MutableRefObject<HTMLInputElement | null>;
  setDiscountInput: (value: string) => void;
  handleDiscountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTaxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSpecialDiscountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDiscountBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleTaxBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleSpecialDiscountBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleFocusSelect: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleDiscountKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleTaxKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSpecialDiscountKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  handleBonusChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBonusBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleBonusKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleBnoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBnoBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleBnoKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleExpireDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleExpireDateBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleExpireDateFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleExpireDateKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isPending: boolean;
}

export const DiscountBatchSection = memo(
  ({
    selectedItem,
    suppliar,
    qtyInput,
    taxInput,
    discountInput,
    specialDiscountInput,
    bonusInput,
    bnoInput,
    expireDate,
    discountRef,
    taxRef,
    specialDiscountRef,
    bonusRef,
    bnoRef,
    expireDateRef,
    setDiscountInput,
    handleDiscountChange,
    handleTaxChange,
    handleSpecialDiscountChange,
    handleDiscountBlur,
    handleTaxBlur,
    handleSpecialDiscountBlur,
    handleFocusSelect,
    handleDiscountKeyDown,
    handleTaxKeyDown,
    handleSpecialDiscountKeyDown,
    handleBonusChange,
    handleBonusBlur,
    handleBonusKeyDown,
    handleBnoChange,
    handleBnoBlur,
    handleBnoKeyDown,
    handleExpireDateChange,
    handleExpireDateBlur,
    handleExpireDateFocus,
    handleExpireDateKeyDown,
    isPending,
  }: DiscountBatchSectionProps) => {
    const [purchasePrice, setPurchasePrice] = useState<string>('');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);

    // Calculate price after tax
    const getPriceAfterTax = useCallback(
      (basePrice?: number): number => {
        if (!basePrice) return 0;
        const tax = parseFloat(taxInput) || 0;
        return basePrice - tax;
      },
      [taxInput],
    );

    // Calculate discount from purchase price
    const calculateDiscountFromPrice = useCallback(
      (price: number) => {
        if (!selectedItem?.Price) return;
        const priceAfterTax = getPriceAfterTax(selectedItem.Price);
        const discount = ((priceAfterTax - price) / priceAfterTax) * 100;
        setDiscountInput(round(discount, 3).toString());
      },
      [getPriceAfterTax, selectedItem, setDiscountInput],
    );

    // Add handler for purchase price input
    const handlePurchasePriceChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d{0,3}$/.test(value)) {
          const numValue = parseFloat(value);
          const priceAfterTax = getPriceAfterTax(selectedItem?.Price);
          if (isNaN(numValue) || numValue <= priceAfterTax) {
            setPurchasePrice(value);
          }
        }
      },
      [getPriceAfterTax, selectedItem],
    );

    // Add handler for purchase price blur
    const handlePurchasePriceBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && selectedItem?.Price) {
          const priceAfterTax = getPriceAfterTax(selectedItem.Price);
          if (value > priceAfterTax) {
            toast.error('سعر الشراء يجب أن يكون أقل من السعر بعد الضريبة');
            setPurchasePrice('');
            return;
          }
          calculateDiscountFromPrice(value);
        }
        setPurchasePrice('');
      },
      [calculateDiscountFromPrice, getPriceAfterTax, selectedItem],
    );

    // Add handler for purchase price key down
    const handlePurchasePriceKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const value = parseFloat(e.currentTarget.value);
          if (!isNaN(value) && selectedItem?.Price) {
            const priceAfterTax = getPriceAfterTax(selectedItem.Price);
            if (value > priceAfterTax) {
              toast.error('سعر الشراء يجب أن يكون أقل من السعر بعد الضريبة');
              setPurchasePrice('');
              return;
            }
            calculateDiscountFromPrice(value);
          }
          setPurchasePrice('');
          // Close popover
          setIsPopoverOpen(false);
          // Focus on discount input
          if (discountRef.current) {
            discountRef.current.focus();
            discountRef.current.select();
          }
        }
      },
      [calculateDiscountFromPrice, discountRef, getPriceAfterTax, selectedItem],
    );

    // Calculate price when popover opens
    useEffect(() => {
      if (isPopoverOpen && selectedItem?.Price && discountInput) {
        const discount = parseFloat(discountInput);
        const priceAfterTax = getPriceAfterTax(selectedItem.Price);
        const price = priceAfterTax * (1 - discount / 100);
        setPurchasePrice(round(price, 3).toString());
      }
    }, [isPopoverOpen, selectedItem?.Price, discountInput, getPriceAfterTax]);

    // Memoize the price calculation to avoid recalculation on every render
    const basePrice = useMemo(() => {
      if (!selectedItem?.Price) return '-';

      const basePrice = selectedItem.Price;
      const tax = parseFloat(taxInput) || 0;
      const priceAfterTax = basePrice - tax;

      return round(priceAfterTax, 2).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
    }, [selectedItem?.Price, taxInput]);

    // Memoize the total calculation
    const totalPrice = useMemo(() => {
      if (!selectedItem?.Price || !qtyInput || !discountInput) return '-';

      const regularDiscount = parseFloat(discountInput) || 0;
      const specialDiscount = parseFloat(specialDiscountInput) || 0;
      const priceAfterTax = getPriceAfterTax(selectedItem.Price);

      // Calculate total discount (regular + special compound discount)
      let totalDiscount = regularDiscount;
      if (specialDiscount > 0) {
        // Apply compound discount formula
        totalDiscount =
          100 * (1 - (1 - regularDiscount / 100) * (1 - specialDiscount / 100));
      }

      // Calculate total after discount
      const totalAfterDiscount =
        ((parseInt(qtyInput) * (100 - totalDiscount)) / 100) * priceAfterTax;

      return round(totalAfterDiscount, 3).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
    }, [
      selectedItem?.Price,
      qtyInput,
      discountInput,
      specialDiscountInput,
      getPriceAfterTax,
    ]);

    const isDisabled = !selectedItem || !suppliar || isPending;

    // Create a compatible item object for the dialog
    const createCompatibleItem = (): IOneItemInPrepareForRequest | null => {
      if (!selectedItem) return null;

      return {
        HealthyItemID: selectedItem.ID,
        ClientItemName: selectedItem.Name,
        Discount: parseFloat(discountInput || '0'),
        // Add minimum required fields with default values
        itemBodyID: '',
        isLastUpdate: false,
        itemName: selectedItem.Name,
        healthyItemPrice: selectedItem.Price,
        Qty: 0,
        TotalAfterDiscount: 0,
        Price: selectedItem.Price,
        DiscountEdit: 0,
        DiscountStatus: 'static',
        GroupItems: [],
        CompetitorsDiscounts: [],
        WholeSaleDiscount: 0,
        WholeSaleStock: 0,
        WholeSaleSales: 0,
        WholeSaleSalesStatus: 'needStock',
        WholeSaleDiff: 0,
        RetailDiscount: 0,
        RetailStock: 0,
        RetailSales: 0,
        RetailStatus: 'needStock',
        RetailDiff: 0,
        HealthyDiscountIN: 0,
        bestDiscount: 0,
        minDiscount: 0,
        underRequests: [],
        totalUnderRequests: 0,
      } as IOneItemInPrepareForRequest;
    };

    return (
      <>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          <div>
            <Label htmlFor="tax">الضريبة</Label>
            <Input
              id="tax"
              type="number"
              step="0.01"
              min="0"
              className="text-xl"
              ref={taxRef}
              value={taxInput}
              onChange={handleTaxChange}
              onBlur={handleTaxBlur}
              onFocus={handleFocusSelect}
              onKeyDown={handleTaxKeyDown}
              placeholder="0"
              disabled={isDisabled}
            />
          </div>
          <div>
            <Label htmlFor="item-name">الاساسي</Label>
            <div className="flex h-10 w-full items-center rounded-md border border-input bg-gray-100 px-3 py-2 text-xl text-gray-700">
              {basePrice}
            </div>
          </div>
          <div className="sm:col-span-2 md:col-span-1">
            <Label htmlFor="discount">الخصم </Label>
            <div className="flex gap-2">
              <Input
                id="discount"
                type="number"
                step="0.001"
                min="0"
                className="text-xl"
                max="100"
                ref={discountRef}
                value={discountInput}
                onChange={handleDiscountChange}
                onBlur={handleDiscountBlur}
                onFocus={handleFocusSelect}
                onKeyDown={handleDiscountKeyDown}
                placeholder="0 - 100"
                disabled={isDisabled}
              />
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 flex-shrink-0"
                    disabled={isDisabled}
                  >
                    <Calculator className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="purchase-price">سعر الشراء</Label>
                      <Input
                        id="purchase-price"
                        type="number"
                        step="0.001"
                        min="0"
                        value={purchasePrice}
                        onChange={handlePurchasePriceChange}
                        onBlur={handlePurchasePriceBlur}
                        onKeyDown={handlePurchasePriceKeyDown}
                        placeholder="أدخل سعر الشراء"
                        className="text-xl"
                        disabled={isPending}
                      />
                      <div className="text-sm text-muted-foreground">
                        اضغط Enter بعد إدخال السعر لحساب الخصم
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      سيتم حساب الخصم تلقائياً بناءً على سعر الشراء
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div>
            <Label htmlFor="special-discount">ت.دفع</Label>
            <Input
              id="special-discount"
              type="text"
              ref={specialDiscountRef}
              value={specialDiscountInput}
              onChange={handleSpecialDiscountChange}
              onBlur={handleSpecialDiscountBlur}
              onFocus={handleFocusSelect}
              onKeyDown={handleSpecialDiscountKeyDown}
              placeholder="مثال: 5 + 2"
              disabled={isDisabled}
            />
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <Label>إجمالي</Label>
            <div className="flex h-10 w-full items-center overflow-hidden text-ellipsis rounded-md border border-input bg-gray-100 px-3 py-2 text-xl text-gray-700">
              {totalPrice}
            </div>
          </div>
          <div>
            <Label htmlFor="bonus">البونص</Label>
            <Input
              id="bonus"
              type="number"
              min="0"
              ref={bonusRef}
              value={bonusInput}
              className="text-xl"
              onChange={handleBonusChange}
              onBlur={handleBonusBlur}
              onFocus={handleFocusSelect}
              onKeyDown={handleBonusKeyDown}
              placeholder="0"
              disabled={isDisabled}
            />
          </div>
          <div>
            <Label htmlFor="bno">الباتش</Label>
            <Input
              id="bno"
              type="text"
              ref={bnoRef}
              value={bnoInput}
              className="text-xl"
              onChange={handleBnoChange}
              onBlur={handleBnoBlur}
              onFocus={handleFocusSelect}
              onKeyDown={handleBnoKeyDown}
              placeholder="رقم الباتش"
              pattern="[a-zA-Z0-9]*"
              inputMode="text"
              disabled={isDisabled}
            />
          </div>
          <div className="relative">
            <Label htmlFor="expire-date">تاريخ الصلاحية</Label>
            <div className="flex items-center gap-2">
              <Input
                id="expire-date"
                type="text"
                ref={expireDateRef}
                value={expireDate}
                className="min-w-32 text-xl"
                onChange={handleExpireDateChange}
                onBlur={handleExpireDateBlur}
                onFocus={handleExpireDateFocus}
                onKeyDown={handleExpireDateKeyDown}
                placeholder="MM/YYYY"
                maxLength={7}
                disabled={isDisabled}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-blue-800 dark:hover:bg-blue-950/50 dark:hover:text-blue-400"
                onClick={() => setShowPurchaseHistory(true)}
                disabled={!selectedItem}
                title="تاريخ الوارد"
              >
                <History className="mr-1 h-4 w-4" />
                تاريخ الوارد
              </Button>
            </div>
          </div>
        </div>

        {/* Purchase History Dialog */}
        {showPurchaseHistory && (
          <PurchaseHistoryDialog
            isOpen={showPurchaseHistory}
            closeFn={() => setShowPurchaseHistory(false)}
            item={createCompatibleItem()}
          />
        )}
      </>
    );
  },
);

DiscountBatchSection.displayName = 'DiscountBatchSection';
